// src/components/sections/ExcellenceCenters.js
import React from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaHeartbeat, FaBaby, FaFlask, FaProcedures,
    FaHospital, FaUserMd, FaStar, FaChartLine
} from 'react-icons/fa';

const ExcellenceCenters = () => {
    const centers = [
        {
            title: "High-Risk Pregnancy Care",
            icon: <FaHeartbeat />,
            description: "Specialized monitoring and management for complicated pregnancies",
            features: ["Fetal Monitoring", "Multi-disciplinary Team", "24/7 Specialist Availability"],
            color: "#FF7F50",
            badge: "Advanced"
        },
        {
            title: "IVF & Advanced Fertility",
            icon: <FaFlask />,
            description: "State-of-the-art reproductive technology and fertility treatments",
            features: ["IVF/ICSI", "Egg Freezing", "Genetic Screening"],
            color: "#9370DB",
            badge: "Cutting-edge"
        },
        {
            title: "Neonatal ICU (NICU)",
            icon: <FaBaby />,
            description: "Level III NICU with advanced life support for premature babies",
            features: ["Ventilator Support", "Kangaroo Care", "Parental Counseling"],
            color: "#87CEEB",
            badge: "Level III"
        },
        {
            title: "Minimally Invasive Surgery",
            icon: <FaProcedures />,
            description: "Advanced laparoscopic and robotic surgical procedures",
            features: ["Robotic Surgery", "Faster Recovery", "Less Scarring"],
            color: "#3CB371",
            badge: "Advanced"
        },
        {
            title: "Pediatric ICU",
            icon: <FaHospital />,
            description: "Specialized intensive care for critically ill children",
            features: ["Pediatric Ventilation", "24/7 Specialist", "Child Life Services"],
            color: "#20B2AA",
            badge: "Specialized"
        },
        {
            title: "Child Development Center",
            icon: <FaChartLine />,
            description: "Comprehensive assessment and therapy for developmental needs",
            features: ["Early Intervention", "Autism Care", "Learning Support"],
            color: "#FFD700",
            badge: "Holistic"
        }
    ];

    return (
        <section
            id="excellence"
            className="section-padding"
        >
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Centers of Excellence
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        Specialized care units with advanced technology and expert medical teams
                    </p>
                </motion.div>

                <Row className="g-4">
                    {centers.map((center, index) => (
                        <Col lg={4} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <Card className="healthcare-card h-100 border-0 overflow-hidden" style={{ position: 'relative', zIndex: 2 }}>
                                    <div
                                        className="position-relative"
                                        style={{
                                            height: '150px',
                                            backgroundColor: `${center.color}15`,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <div
                                            className="position-absolute d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                backgroundColor: center.color,
                                                borderRadius: '50%',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                color: 'white',
                                                fontSize: '2.5rem'
                                            }}
                                        >
                                            {center.icon}
                                        </div>
                                        <Badge
                                            className="position-absolute top-0 end-0 m-3"
                                            style={{
                                                backgroundColor: center.color,
                                                fontSize: '0.8rem',
                                                padding: '6px 12px'
                                            }}
                                        >
                                            {center.badge}
                                        </Badge>
                                    </div>

                                    <Card.Body className="p-4">
                                        <Card.Title className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                                            {center.title}
                                        </Card.Title>
                                        <Card.Text className="mb-4" style={{ color: '#666' }}>
                                            {center.description}
                                        </Card.Text>

                                        <div className="mb-4">
                                            <h6 className="fw-bold mb-3" style={{ color: center.color }}>
                                                Key Features:
                                            </h6>
                                            <ul className="list-unstyled">
                                                {center.features.map((feature, idx) => (
                                                    <li key={idx} className="mb-2 d-flex align-items-center">
                                                        <FaStar className="me-2" size={12} style={{ color: center.color }} />
                                                        <span style={{ color: '#555', fontSize: '0.9rem' }}>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="text-center">
                                            <button
                                                className="btn btn-link text-decoration-none fw-bold p-0"
                                                style={{ color: center.color }}
                                            >
                                                Learn About Our Expertise →
                                            </button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="text-center mt-5 pt-4"
                >
                    <div className="p-4 rounded position-relative" style={{
                        backgroundColor: 'var(--soft-yellow)',
                        zIndex: 2,
                        backdropFilter: 'blur(5px)'
                    }}>
                        <FaUserMd size={40} className="mb-3" style={{ color: 'var(--dark-teal)' }} />
                        <h4 className="fw-bold mb-3">Multi-disciplinary Specialist Teams</h4>
                        <p className="mb-0" style={{ maxWidth: '600px', margin: '0 auto' }}>
                            Each center is staffed by dedicated teams of specialists working together
                            to provide comprehensive, coordinated care for complex medical conditions.
                        </p>
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default ExcellenceCenters;